import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { timeEntriesAPI, timeLookupsAPI, timeProfilesAPI, timeReportsAPI } from '../services/api';
import { CreateTimeEntry, TimeCustomerLookup, TimeEntry, TimeEntryFilter, TimeProfile, TimeProjectLookup, TimeProjectTaskLookup, TimeSummaryReport } from '../types';
import { endOfMonthIso, endOfWeekIso, formatDate, formatDurationMinutes, minutesToParts, partsToMinutes, startOfMonthIso, startOfWeekIso, todayIso } from '../utils';
import { extractBadRequestErrorMessage } from '../utils/apiError';
import { resolveEntryProfileId } from '../utils/entryProfile';
import DateInput from './DateInput';
import TableLoadingRow from './TableLoadingRow';
import TimeSelect, { TimeSelectOption } from './TimeSelect';

const ENTRIES_FILTERS_STORAGE_KEY = 'timeEntries.filters';

const billingFilterOptions: TimeSelectOption<string>[] = [
  { value: 'all', label: 'All billing' },
  { value: 'true', label: 'Billable' },
  { value: 'false', label: 'Non-billable' },
];

const getStoredEntryFilters = (): TimeEntryFilter => {
  try {
    const storedFilters = localStorage.getItem(ENTRIES_FILTERS_STORAGE_KEY);
    if (!storedFilters) {
      return { take: 25, skip: 0 };
    }

    const parsedFilters = JSON.parse(storedFilters) as TimeEntryFilter;
    return {
      take: parsedFilters.take || 25,
      skip: parsedFilters.skip || 0,
      startDate: parsedFilters.startDate || undefined,
      endDate: parsedFilters.endDate || undefined,
      timeProfileId: parsedFilters.timeProfileId || undefined,
      customerId: parsedFilters.customerId || undefined,
      projectId: parsedFilters.projectId || undefined,
      isBillable: parsedFilters.isBillable,
      search: parsedFilters.search || undefined,
    };
  } catch {
    return { take: 25, skip: 0 };
  }
};

const storeEntryFilters = (filters: TimeEntryFilter) => {
  try {
    localStorage.setItem(ENTRIES_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors; filters still work for the current session.
  }
};

interface EntryFormState {
  timeProfileId: number;
  customerId: number;
  customerQuery: string;
  projectId: number;
  projectQuery: string;
  projectTaskId: number;
  projectTaskQuery: string;
  workDate: string;
  hours: number | '';
  minutes: number | '';
  isBillable: boolean;
  description: string;
}

const defaultForm: EntryFormState = {
  timeProfileId: 0,
  customerId: 0,
  customerQuery: '',
  projectId: 0,
  projectQuery: '',
  projectTaskId: 0,
  projectTaskQuery: '',
  workDate: todayIso(),
  hours: '',
  minutes: '',
  isBillable: true,
  description: '',
};

const EntriesPage: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [projectTasks, setProjectTasks] = useState<TimeProjectTaskLookup[]>([]);
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [form, setForm] = useState<EntryFormState>(defaultForm);
  const [filters, setFilters] = useState<TimeEntryFilter>(() => getStoredEntryFilters());
  const [filteredSummary, setFilteredSummary] = useState<TimeSummaryReport | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const page = useMemo(() => Math.floor((filters.skip || 0) / (filters.take || 25)) + 1, [filters.skip, filters.take]);
  const totalPages = Math.max(1, Math.ceil(totalCount / (filters.take || 25)));
  const summaryFilters = useMemo(() => {
    const { skip, take, ...appliedFilters } = filters;
    return appliedFilters;
  }, [filters]);
  const filteredProjects = useMemo(() => {
    if (!filters.customerId) {
      return projects;
    }

    return projects.filter(project => project.customerId === filters.customerId);
  }, [filters.customerId, projects]);

  const entryProjects = useMemo(() => {
    if (!form.customerId) {
      return projects;
    }

    return projects.filter(project => project.customerId === form.customerId);
  }, [form.customerId, projects]);

  const formatProjectOption = (project: TimeProjectLookup) => `${project.customerTitle} - ${project.title}`;
  const formatProjectTaskOption = (task: TimeProjectTaskLookup) => task.projectId === form.projectId
    ? task.title
    : `${task.customerTitle} - ${task.projectTitle} - ${task.title}`;

  const entryProjectTasks = useMemo(() => {
    if (!form.projectId) {
      return [];
    }

    return projectTasks.filter(task => task.projectId === form.projectId);
  }, [form.projectId, projectTasks]);

  const applyCustomerQuery = (value: string) => {
    const matchedCustomer = customers.find(customer => customer.title.toLowerCase() === value.trim().toLowerCase());
    setForm(prev => {
      const customerId = matchedCustomer?.id || 0;
      const currentProjectStillMatches = customerId && projects.some(project => project.id === prev.projectId && project.customerId === customerId);

      return {
        ...prev,
        customerId,
        customerQuery: value,
        projectId: currentProjectStillMatches ? prev.projectId : 0,
        projectQuery: currentProjectStillMatches ? prev.projectQuery : '',
        projectTaskId: currentProjectStillMatches ? prev.projectTaskId : 0,
        projectTaskQuery: currentProjectStillMatches ? prev.projectTaskQuery : '',
      };
    });
  };

  const applyProjectQuery = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const matchedProject = entryProjects.find(project =>
      project.title.toLowerCase() === normalizedValue ||
      formatProjectOption(project).toLowerCase() === normalizedValue
    );

    setForm(prev => ({
      ...prev,
      projectId: matchedProject?.id || 0,
      projectQuery: value,
      projectTaskId: 0,
      projectTaskQuery: '',
      customerId: matchedProject?.customerId || prev.customerId,
      customerQuery: matchedProject?.customerTitle || prev.customerQuery,
    }));
  };

  const applyProjectTaskQuery = (value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const matchedTask = entryProjectTasks.find(task =>
      task.title.toLowerCase() === normalizedValue ||
      formatProjectTaskOption(task).toLowerCase() === normalizedValue
    );

    setForm(prev => ({
      ...prev,
      projectTaskId: matchedTask?.id || 0,
      projectTaskQuery: value,
    }));
  };

  const loadLookups = async () => {
    const [profileData, projectData, customerData, projectTaskData] = await Promise.all([
      timeProfilesAPI.getProfiles(false),
      timeLookupsAPI.getProjects(),
      timeLookupsAPI.getCustomers(),
      timeLookupsAPI.getProjectTasks(),
    ]);
    setProfiles(profileData);
    setProjects(projectData);
    setCustomers(customerData);
    setProjectTasks(projectTaskData);

    setForm(prev => ({
      ...prev,
      timeProfileId: resolveEntryProfileId(profileData, user?.email, isAdmin, prev.timeProfileId),
      customerId: prev.customerId,
      customerQuery: prev.customerQuery,
      projectId: prev.projectId,
      projectQuery: prev.projectQuery,
    }));
  };

  const loadEntries = async () => {
    setLoading(true);
    try {
      const result = await timeEntriesAPI.getEntries(filters);
      setEntries(result.items);
      setTotalCount(result.totalCount);
    } catch {
      toast.error('Entries could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  const loadFilteredSummary = async () => {
    setSummaryLoading(true);
    try {
      const result = await timeReportsAPI.getSummary(summaryFilters);
      setFilteredSummary(result);
    } catch {
      setFilteredSummary(null);
      toast.error('Entry summary could not be loaded');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadLookups().catch(() => toast.error('Lookups could not be loaded'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, user?.email]);

  useEffect(() => {
    storeEntryFilters(filters);
  }, [filters]);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isAdmin]);

  useEffect(() => {
    loadFilteredSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryFilters, isAdmin]);

  const resetForm = () => {
    setEditingId(null);
    setFormOpen(false);
    setForm(prev => ({
      ...defaultForm,
      timeProfileId: resolveEntryProfileId(profiles, user?.email, isAdmin, prev.timeProfileId),
    }));
  };

  const openNewEntry = () => {
    setEditingId(null);
    setForm(prev => ({
      ...defaultForm,
      timeProfileId: resolveEntryProfileId(profiles, user?.email, isAdmin, prev.timeProfileId),
    }));
    setFormOpen(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    const durationMinutes = partsToMinutes(form.hours, form.minutes);
    if (!form.timeProfileId || !form.projectId || durationMinutes <= 0) {
      toast.error('Entry is incomplete');
      return;
    }

    const payload: CreateTimeEntry = {
      timeProfileId: form.timeProfileId,
      projectId: form.projectId,
      projectTaskId: form.projectTaskId || undefined,
      workDate: form.workDate,
      durationMinutes,
      isBillable: form.isBillable,
      description: form.description || undefined,
    };

    setSaving(true);
    try {
      if (editingId) {
        await timeEntriesAPI.updateEntry(editingId, { ...payload, id: editingId });
        toast.success('Entry updated');
      } else {
        await timeEntriesAPI.createEntry(payload);
        toast.success('Entry created');
      }
      resetForm();
      await Promise.all([loadEntries(), loadFilteredSummary()]);
    } catch (error) {
      toast.error(extractBadRequestErrorMessage(error, 'Entry could not be saved'));
    } finally {
      setSaving(false);
    }
  };

  const editEntry = (entry: TimeEntry) => {
    const duration = minutesToParts(entry.durationMinutes);
    const project = projects.find(item => item.id === entry.projectId);
    const customerId = entry.customerId || project?.customerId || 0;
    const customerTitle = entry.customerTitle || project?.customerTitle || '';
    setEditingId(entry.id);
    setForm({
      timeProfileId: entry.timeProfileId,
      customerId,
      customerQuery: customerTitle,
      projectId: entry.projectId || 0,
      projectQuery: project ? formatProjectOption(project) : entry.projectTitle || '',
      projectTaskId: entry.projectTaskId || 0,
      projectTaskQuery: entry.projectTaskTitle || '',
      workDate: entry.workDate.split('T')[0],
      hours: duration.hours,
      minutes: duration.minutes,
      isBillable: entry.isBillable,
      description: entry.description || '',
    });
    setFormOpen(true);
  };

  const copyEntry = (entry: TimeEntry) => {
    const duration = minutesToParts(entry.durationMinutes);
    const project = projects.find(item => item.id === entry.projectId);
    const customerId = entry.customerId || project?.customerId || 0;
    const customerTitle = entry.customerTitle || project?.customerTitle || '';
    setEditingId(null);
    setForm({
      timeProfileId: entry.timeProfileId,
      customerId,
      customerQuery: customerTitle,
      projectId: entry.projectId || 0,
      projectQuery: project ? formatProjectOption(project) : entry.projectTitle || '',
      projectTaskId: entry.projectTaskId || 0,
      projectTaskQuery: entry.projectTaskTitle || '',
      workDate: entry.workDate.split('T')[0],
      hours: duration.hours,
      minutes: duration.minutes,
      isBillable: entry.isBillable,
      description: entry.description || '',
    });
    setFormOpen(true);
  };

  const deleteEntry = async () => {
    if (!entryToDelete) {
      return;
    }

    try {
      await timeEntriesAPI.deleteEntry(entryToDelete.id);
      toast.success('Entry deleted');
      setEntryToDelete(null);
      await Promise.all([loadEntries(), loadFilteredSummary()]);
    } catch {
      toast.error('Entry could not be deleted');
    }
  };

  const changePage = (direction: -1 | 1) => {
    const take = filters.take || 25;
    const nextPage = Math.min(totalPages, Math.max(1, page + direction));
    setFilters(prev => ({ ...prev, skip: (nextPage - 1) * take }));
  };

  const getEntryCustomerTitle = (entry: TimeEntry) => {
    if (entry.customerTitle) {
      return entry.customerTitle;
    }

    return projects.find(project => project.id === entry.projectId)?.customerTitle || 'Unassigned / internal';
  };

  const getDeleteEntryTitle = (entry: TimeEntry) => {
    const projectTitle = entry.projectTitle || 'Unassigned / internal';
    return `${formatDate(entry.workDate)} • ${projectTitle} • ${formatDurationMinutes(entry.durationMinutes)}`;
  };

  const getEntryProjectColor = (entry: TimeEntry) => entry.projectColor || projects.find(project => project.id === entry.projectId)?.color;

  const projectBadge = (label: string, color?: string) => (
    <span className="project-color-badge" style={{ '--project-color': color || '#64748b' } as React.CSSProperties}>
      <span className="project-color-dot" aria-hidden="true" />
      {label}
    </span>
  );

  const updateCustomerFilter = (customerId: number | undefined) => {
    setFilters(prev => {
      const selectedProjectStillMatches = !customerId || !prev.projectId ||
        projects.some(project => project.id === prev.projectId && project.customerId === customerId);

      return {
        ...prev,
        customerId,
        projectId: selectedProjectStillMatches ? prev.projectId : undefined,
        skip: 0,
      };
    });
  };

  const applyDateRangeFilter = (startDate: string, endDate: string) => {
    setFilters(prev => ({ ...prev, startDate, endDate, skip: 0 }));
  };

  const applyThisWeekFilter = () => {
    const today = new Date();
    applyDateRangeFilter(startOfWeekIso(today), endOfWeekIso(today));
  };

  const applyLastWeekFilter = () => {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    applyDateRangeFilter(startOfWeekIso(lastWeek), endOfWeekIso(lastWeek));
  };

  const applyThisMonthFilter = () => {
    const today = new Date();
    applyDateRangeFilter(startOfMonthIso(today), endOfMonthIso(today));
  };

  const applyLastMonthFilter = () => {
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1, 1);
    applyDateRangeFilter(startOfMonthIso(lastMonth), endOfMonthIso(lastMonth));
  };

  const entryForm = (
    <form className="time-form entry-form" onSubmit={handleSubmit}>
      {isAdmin && (
        <div className="form-group">
          <label className="form-label" htmlFor="profile">Member</label>
          <TimeSelect
            inputId="profile"
            ariaLabel="Member"
            options={[
              { value: 0, label: 'Member' },
              ...profiles.map(profile => ({ value: profile.id, label: profile.displayName })),
            ]}
            value={form.timeProfileId}
            onChange={(value) => setForm(prev => ({ ...prev, timeProfileId: Number(value || 0) }))}
            placeholder="Member"
          />
        </div>
      )}
      <div className="entry-meta-grid">
        <div className="form-group">
          <label className="form-label" htmlFor="date">Date</label>
          <DateInput
            id="date"
            className="form-control"
            value={form.workDate}
            onChange={(value) => setForm(prev => ({ ...prev, workDate: value }))}
            required
          />
        </div>
        <div className="duration-grid entry-duration-grid">
          <div className="form-group">
            <label className="form-label" htmlFor="hours">Hours</label>
            <input
              id="hours"
              className="form-control"
              type="number"
              min="0"
              max="24"
              value={form.hours}
              onChange={(event) => setForm(prev => ({ ...prev, hours: event.target.value === '' ? '' : Number(event.target.value) }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="minutes">Minutes</label>
            <input
              id="minutes"
              className="form-control"
              type="number"
              min="0"
              max="59"
              value={form.minutes}
              onChange={(event) => setForm(prev => ({ ...prev, minutes: event.target.value === '' ? '' : Number(event.target.value) }))}
            />
          </div>
        </div>
      </div>
      <div className="entry-lookup-grid entry-lookup-grid-wide">
        <div className="form-group entry-client-field">
          <label className="form-label" htmlFor="entry-client">Client</label>
          <TimeSelect
            inputId="entry-client"
            ariaLabel="Client"
            options={customers.map(customer => ({ value: customer.id, label: customer.title }))}
            value={form.customerId || null}
            onChange={(_, option) => applyCustomerQuery(option?.label || '')}
            placeholder="Start typing a client"
            noOptionsMessage="No clients"
          />
        </div>
        <div className="form-group entry-project-field">
          <label className="form-label" htmlFor="project">Project</label>
          <TimeSelect
            inputId="project"
            ariaLabel="Project"
            options={entryProjects.map(project => ({ value: project.id, label: formatProjectOption(project) }))}
            value={form.projectId || null}
            onChange={(_, option) => applyProjectQuery(option?.label || '')}
            placeholder={form.customerId ? 'Start typing a project' : 'Select a client first'}
            noOptionsMessage="No projects"
          />
        </div>
        <div className="form-group entry-task-field">
          <label className="form-label" htmlFor="entry-task">Task <span className="optional-label">optional</span></label>
          <TimeSelect
            inputId="entry-task"
            ariaLabel="Task"
            options={entryProjectTasks.map(task => ({ value: task.id, label: formatProjectTaskOption(task) }))}
            value={form.projectTaskId || null}
            onChange={(_, option) => applyProjectTaskQuery(option?.label || '')}
            placeholder={form.projectId ? 'Start typing a task' : 'Select a project first'}
            isClearable
            isDisabled={!form.projectId || entryProjectTasks.length === 0}
            noOptionsMessage="No tasks"
          />
        </div>
      </div>
      <label className="toggle-row entry-toggle-row">
        <input
          type="checkbox"
          checked={form.isBillable}
          onChange={(event) => setForm(prev => ({ ...prev, isBillable: event.target.checked }))}
        />
        Billable
      </label>
      <div className="form-group entry-description-field">
        <label className="form-label" htmlFor="description">Description</label>
        <textarea
          id="description"
          className="form-control entry-description-control"
          rows={5}
          value={form.description}
          onChange={(event) => setForm(prev => ({ ...prev, description: event.target.value }))}
        />
      </div>
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={saving} aria-busy={saving}>
          {saving ? (
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          ) : (
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
          )}
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button className="btn btn-secondary" type="button" onClick={resetForm}>
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Entries</h1>
          <p>{totalCount} records</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={openNewEntry}>
          <i className="fa-solid fa-plus" aria-hidden="true" />
          New entry
        </button>
      </div>

      {isFormOpen && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              resetForm();
            }
          }}
        >
          <section className="time-modal entry-modal entry-modal-wide" role="dialog" aria-modal="true" aria-labelledby="entry-dialog-title">
            <div className="time-modal-header">
              <h5 id="entry-dialog-title" className="card-title">{editingId ? 'Edit entry' : 'New entry'}</h5>
              <button className="icon-button" type="button" onClick={resetForm} aria-label="Close entry form">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              {entryForm}
            </div>
          </section>
        </div>
      )}

      {entryToDelete && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setEntryToDelete(null);
            }
          }}
        >
          <section className="time-modal confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="delete-entry-dialog-title">
            <div className="time-modal-header">
              <h5 id="delete-entry-dialog-title" className="card-title">Delete entry?</h5>
              <button className="icon-button" type="button" onClick={() => setEntryToDelete(null)} aria-label="Close delete confirmation">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              <p className="confirmation-text">Are you sure you want to delete this time entry?</p>
              <p className="confirmation-detail">{getDeleteEntryTitle(entryToDelete)}</p>
              <div className="form-actions confirmation-actions">
                <button className="btn btn-secondary" type="button" onClick={() => setEntryToDelete(null)}>
                  Cancel
                </button>
                <button className="btn btn-danger" type="button" onClick={deleteEntry}>
                  <i className="fa-solid fa-trash" aria-hidden="true" />
                  Delete entry
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      <section className="card">
          <div className="card-header">
            <h5 className="card-title">Entry list</h5>
          </div>
          <div className="card-body">
            <div className="filter-bar entries-filter-bar" data-testid="entries-filter-bar">
              <DateInput
                className="form-control filter-date-control filter-start-date-control"
                aria-label="Start date filter"
                value={filters.startDate || ''}
                onChange={(value) => setFilters(prev => ({ ...prev, startDate: value, skip: 0 }))}
              />
              <DateInput
                className="form-control filter-date-control filter-end-date-control"
                aria-label="End date filter"
                value={filters.endDate || ''}
                onChange={(value) => setFilters(prev => ({ ...prev, endDate: value, skip: 0 }))}
              />
              <div className="quick-date-filter-row filter-quick-date-control" aria-label="Quick date filters">
                <button className="btn btn-secondary btn-sm" type="button" onClick={applyThisWeekFilter}>This week</button>
                <button className="btn btn-secondary btn-sm" type="button" onClick={applyLastWeekFilter}>Last week</button>
                <button className="btn btn-secondary btn-sm" type="button" onClick={applyThisMonthFilter}>This month</button>
                <button className="btn btn-secondary btn-sm" type="button" onClick={applyLastMonthFilter}>Last month</button>
              </div>
              <TimeSelect
                inputId="entries-employee-filter"
                className="filter-employee-control"
                ariaLabel="Member filter"
                options={[
                  { value: 0, label: 'All members' },
                  ...profiles.map(profile => ({ value: profile.id, label: profile.displayName })),
                ]}
                value={filters.timeProfileId || 0}
                onChange={(value) => setFilters(prev => ({ ...prev, timeProfileId: Number(value || 0) || undefined, skip: 0 }))}
                placeholder="All members"
              />
              <TimeSelect
                inputId="entries-customer-filter"
                className="filter-customer-control"
                ariaLabel="Customer filter"
                options={[
                  { value: 0, label: 'All customers' },
                  ...customers.map(customer => ({ value: customer.id, label: customer.title })),
                ]}
                value={filters.customerId || 0}
                onChange={(value) => updateCustomerFilter(Number(value || 0) || undefined)}
                placeholder="All customers"
              />
              <TimeSelect
                inputId="entries-project-filter"
                className="filter-project-control"
                ariaLabel="Project filter"
                options={[
                  { value: 0, label: 'All projects' },
                  ...filteredProjects.map(project => ({ value: project.id, label: `${project.customerTitle} - ${project.title}` })),
                ]}
                value={filters.projectId || 0}
                onChange={(value) => setFilters(prev => ({ ...prev, projectId: Number(value || 0) || undefined, skip: 0 }))}
                placeholder="All projects"
              />
              <TimeSelect
                inputId="entries-billing-filter"
                className="filter-billing-control"
                ariaLabel="Billing filter"
                options={billingFilterOptions}
                value={filters.isBillable === undefined ? 'all' : String(filters.isBillable)}
                onChange={(value) => setFilters(prev => ({
                  ...prev,
                  isBillable: value === 'all' ? undefined : value === 'true',
                  skip: 0,
                }))}
                placeholder="All billing"
              />
              <input
                className="form-control filter-search-control"
                aria-label="Search entries"
                placeholder="Search"
                value={filters.search || ''}
                onChange={(event) => setFilters(prev => ({ ...prev, search: event.target.value, skip: 0 }))}
              />
            </div>
            <div className="entries-filter-summary" role="status" aria-label="Filtered entries summary">
              <div>
                <span>Total Hours</span>
                <strong>{summaryLoading ? 'Calculating...' : formatDurationMinutes(filteredSummary?.totalMinutes || 0)}</strong>
              </div>
              <div>
                <span>Filtered entries</span>
                <strong>{summaryLoading ? '—' : filteredSummary?.entryCount || 0}</strong>
              </div>
              <div>
                <span>Billable</span>
                <strong>{summaryLoading ? '—' : formatDurationMinutes(filteredSummary?.billableMinutes || 0)}</strong>
              </div>
              <div>
                <span>Non-billable</span>
                <strong>{summaryLoading ? '—' : formatDurationMinutes(filteredSummary?.nonBillableMinutes || 0)}</strong>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Date</th>
                    {isAdmin && <th>Member</th>}
                    <th>Client</th>
                    <th>Project</th>
                    <th>Task</th>
                    <th>Description</th>
                    <th>Hours</th>
                    <th>Billable</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <TableLoadingRow colSpan={isAdmin ? 9 : 8} label="Loading entries..." />}
                  {!loading && entries.map(entry => (
                    <tr key={entry.id}>
                      <td data-label="Date">{formatDate(entry.workDate)}</td>
                      {isAdmin && <td data-label="Member">{entry.timeProfileName}</td>}
                      <td data-label="Client">{getEntryCustomerTitle(entry)}</td>
                      <td data-label="Project">{projectBadge(entry.projectTitle || 'Unassigned / internal', getEntryProjectColor(entry))}</td>
                      <td data-label="Task">{entry.projectTaskTitle || '—'}</td>
                      <td data-label="Description">{entry.description || '—'}</td>
                      <td data-label="Hours">{formatDurationMinutes(entry.durationMinutes)}</td>
                      <td data-label="Billable">{entry.isBillable ? 'Yes' : 'No'}</td>
                      <td data-label="Actions">
                        <div className="action-row">
                          <button className="action-btn" type="button" onClick={() => editEntry(entry)} title="Edit" aria-label={isAdmin ? `Edit entry for ${entry.timeProfileName}` : 'Edit entry'}>
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button className="action-btn" type="button" onClick={() => copyEntry(entry)} title="Copy" aria-label={isAdmin ? `Copy entry for ${entry.timeProfileName}` : 'Copy entry'}>
                            <i className="fa-solid fa-copy" aria-hidden="true" />
                          </button>
                          <button className="action-btn danger" type="button" onClick={() => setEntryToDelete(entry)} title="Delete" aria-label={isAdmin ? `Delete entry for ${entry.timeProfileName}` : 'Delete entry'}>
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && entries.length === 0 && (
                    <tr>
                      <td colSpan={isAdmin ? 9 : 8}><div className="empty-state">No entries</div></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pager-row">
                <span>Page {page} of {totalPages}</span>
                <div className="action-row">
                  <button className="btn btn-secondary btn-sm" type="button" disabled={page <= 1} onClick={() => changePage(-1)}>
                    <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                  </button>
                  <button className="btn btn-secondary btn-sm" type="button" disabled={page >= totalPages} onClick={() => changePage(1)}>
                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
      </section>
    </div>
  );
};

export default EntriesPage;
